import { Children, forwardRef, useCallback } from "react";
import { augmentElement, isNil, mergeProps, resolveChildren, useMergedRefs } from "../../shared";
import { Overlay, OverlayArrow, usePopup } from "../../overlay";
import { PopoverTriggerContext } from "./PopoverTriggerContext";
import { useResponsiveValue, useThemeContext } from "../../styling";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultElement = "div";
export function InnerPopoverTrigger({
  allowFlip = true,
  allowPreventOverflow = true,
  as = DefaultElement,
  children,
  containerElement,
  defaultOpen,
  dismissable = true,
  forwardedRef,
  id,
  onOpenChange,
  open,
  position: positionProp = "bottom",
  zIndex = 10000,
  ...rest
}) {
  const positionValue = useResponsiveValue(positionProp);
  const {
    themeAccessor
  } = useThemeContext();
  const overlayRef = useMergedRefs(forwardedRef);
  const {
    arrowProps,
    isOpen,
    overlayProps,
    setIsOpen,
    triggerProps
  } = usePopup("dialog", {
    allowFlip,
    allowPreventOverflow,
    boundaryElement: containerElement,
    defaultOpen,
    hasArrow: true,
    hideOnEscape: true,
    hideOnLeave: false,
    hideOnOutsideClick: dismissable,
    id,
    onOpenChange,
    open,
    position: positionValue,
    restoreFocus: true,
    trigger: "click"
  });
  const close = useCallback(event => {
    setIsOpen(event, false);
  }, [setIsOpen]);
  const [trigger, popover] = Children.toArray(resolveChildren(children, {
    close
  }));

  if (isNil(trigger) || isNil(popover)) {
    throw new Error("A popover trigger must have exactly 2 children.");
  }

  const triggerMarkup = augmentElement(trigger, // Since we provide a "close" function to the render function we can't provide a "disabled" prop to usePopup. Therefore, we handle disabled manually.
  trigger.props.disabled ? {} : triggerProps);
  return /*#__PURE__*/_jsxs(PopoverTriggerContext.Provider, {
    value: {
      close,
      isOpen
    },
    children: [triggerMarkup, /*#__PURE__*/_jsxs(Overlay, { ...mergeProps(rest, {
        as,
        borderOffset: themeAccessor.getSpace(3),
        className: "o-ui-popover-overlay",
        ref: overlayRef,
        zIndex
      }, overlayProps),
      children: [popover, /*#__PURE__*/_jsx(OverlayArrow, { ...arrowProps
      })]
    })]
  });
}
InnerPopoverTrigger.defaultElement = DefaultElement;
export const PopoverTrigger = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerPopoverTrigger, { ...props,
  forwardedRef: ref
}));